// ========================================
// MODÈLES DE PAIEMENT — used by Expense path
// Constants + Payment record (receipt id / status helpers)
// ========================================

import isoStandardIdGenerator from '../utils/ISOStandardIdGenerator';
import { PAYMENT_METHODS as CENTRALIZED_PAYMENT_METHODS } from '../services/paymentMethodService';

/** Payment method keys (aligned with paymentMethodService) */
export const PAYMENT_METHODS = Object.keys(CENTRALIZED_PAYMENT_METHODS).reduce((acc, key) => {
  acc[key.toUpperCase()] = key;
  return acc;
}, {});

export const PAYMENT_STATUS = {
  PENDING: 'pending',
  PARTIAL: 'partial',
  PAID: 'paid',
  OVERDUE: 'overdue',
  CANCELLED: 'cancelled'
};

export const TRANSACTION_TYPES = {
  ORDER_PAYMENT: 'order_payment',
  PURCHASE_PAYMENT: 'purchase_payment',
  CREDIT_SALE: 'credit_sale',
  INVOICE_PAYMENT: 'invoice_payment',
  EXPENSE_PAYMENT: 'expense_payment'
};

export const PAYMENT_TYPES = {
  FULL_PAYMENT: 'full_payment',
  PARTIAL_PAYMENT: 'partial_payment',
  CREDIT_PAYMENT: 'credit_payment'
};

export class Payment {
  constructor(data = {}) {
    this.id = data.id || this.generateId();
    this.amount = data.amount || 0;
    this.totalAmount = data.totalAmount || data.amount || 0;
    this.paymentMethod = data.paymentMethod || PAYMENT_METHODS.CASH;
    this.paymentType = data.paymentType || PAYMENT_TYPES.FULL_PAYMENT;
    this.status = data.status || PAYMENT_STATUS.PENDING;
    this.transactionType = data.transactionType || TRANSACTION_TYPES.ORDER_PAYMENT;
    this.referenceId = data.referenceId || null;
    this.customerId = data.customerId || null;
    this.supplierId = data.supplierId || null;
    this.workerId = data.workerId || null;
    this.paymentDate = data.paymentDate || new Date().toISOString();
    this.dueDate = data.dueDate || null;
    this.notes = data.notes || '';
    this.receiptNumber = data.receiptNumber || this.resolveReceiptNumber(data);
    this.createdAt = data.createdAt || new Date().toISOString();
    this.updatedAt = data.updatedAt || new Date().toISOString();
    this.creditLimit = data.creditLimit || 0;
    this.interestRate = data.interestRate || 0;
    this.creditTerms = data.creditTerms || null;
  }

  generateId() {
    return 'PAY_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  resolveReceiptNumber(data = {}) {
    const venteRef =
      data.venteId ||
      data.orderId ||
      (data.referenceId && /^VENTE-\d{4}-\d+$/i.test(String(data.referenceId))
        ? data.referenceId
        : null);
    if (venteRef) {
      return isoStandardIdGenerator.generateReceiptFromVente(String(venteRef));
    }
    return this.generateReceiptNumber();
  }

  generateReceiptNumber() {
    try {
      return isoStandardIdGenerator.generateReceiptNumber();
    } catch {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const random = Math.random().toString(36).substr(2, 4).toUpperCase();
      return `REC-${year}${month}${day}-${random}`;
    }
  }

  getRemainingAmount(totalAmount) {
    if (totalAmount != null) {
      return Math.max(0, totalAmount - this.amount);
    }
    return Math.max(0, this.totalAmount - this.amount);
  }

  isOverdue() {
    if (!this.dueDate) return false;
    return new Date() > new Date(this.dueDate) && this.status !== PAYMENT_STATUS.PAID;
  }

  getPaymentStatus(totalAmount) {
    if (this.status === PAYMENT_STATUS.CANCELLED) return PAYMENT_STATUS.CANCELLED;
    if (this.isOverdue()) return PAYMENT_STATUS.OVERDUE;
    if (this.amount >= totalAmount) return PAYMENT_STATUS.PAID;
    if (this.amount > 0) return PAYMENT_STATUS.PARTIAL;
    return PAYMENT_STATUS.PENDING;
  }

  isFullPayment() {
    return this.paymentType === PAYMENT_TYPES.FULL_PAYMENT;
  }

  isPartialPayment() {
    return this.paymentType === PAYMENT_TYPES.PARTIAL_PAYMENT;
  }

  isCreditPayment() {
    return this.paymentType === PAYMENT_TYPES.CREDIT_PAYMENT;
  }

  getPaymentPercentage() {
    if (this.totalAmount === 0) return 0;
    return Math.min(100, (this.amount / this.totalAmount) * 100);
  }
}
